'use strict';

/* eslint no-empty: 0 */

define('vb/action/builtin/editorUrlAction',[
  'vb/action/action',
  'vb/private/log',
  'vb/private/constants',
  'vb/private/configuration',
  'vb/private/utils',
  'vb/private/stateManagement/router',
],
(Action, Log, Constants, Configuration, Utils, Router) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */

  const logger = Log.getLogger('/vb/action/builtin/editorUrlAction');
  const PARAMS = {
    APP_NAME: 'app',
    APP_URL: 'appUrl',
    EXT_ID: 'extId',
    EXT_MNGR: 'extMngr',
    PAGE_URL: 'pageUrl',
    DOMAIN: 'domain',
    BASE: 'base',
    SANDBOX: 'sandbox',
    STRIPE: 'stripe',
    PAGE_CONT: 'pageContainer',
    PAGE_PATH: 'pagePath',
    COMPONENT_ID: 'componentId',
    DYNAMIC_LAYOUT: 'dynamicLayout',
    RULESET: 'ruleset',
    PODNAME: 'podName',
  };

  // the value of the _h parameter
  const SAAS = 'saas';

  /**
   * This action is used to build the URL of the VB editor from an application at runtime. It gathers
   * multiple pieces of information and returns a URL with request parameters representing various
   * contextual info needed by the VB editor.
   * The base URL pointing to the editor location is either passed as an argument to the action or
   * has to be defined in the EDITOR_URL property of the vbInitConfig global object. If this value
   * is not available, the action will abort with an error.
   * Depending if the dynamicLayout request parameter is defined, the editor will either edit the
   * current page or the ruleset of a specific dynamic component.
   * Here is the list of all request parameters on the generated URL to the editor:
   * <br>
   * app: Web application name
   * appUrl: URL of the application. This will not be on the URL if it is the same as "base"
   * extId: Id of the extension in context, could be more than one value
   * extMngr: Version of the extension manager, v2 or undefined
   * domain: Domain part of the URL of the application
   * base: The URL of the application resources (possibly a CDN path)
   * sandbox: Sandbox ID (if there is one active)
   * stripe: the FA stripe, for example "fscm"
   * pageContainer: Containership path of the current page. This is the page/flow/page path
   * that represent which flow contains the page
   * pagePath: Resource path of the current page. This is a path is relative to the application
   * path (see base param above)
   * componentId: DOM id of the dynamic layout component to edit
   * dynamicLayout: A way to identify the dynamic layout of the component to edit. This is the
   * endpoint property of the DynamicLayoutMetadataProviderDescriptor bound to the layout component
   * to edit for example "leads/get_leads"
   * ruleset: Value of "layout" property of the dynamic layout component to edit
   * <br>
   * @param componentId {String} the id of the component the DOM. The component has to
   * be a dynamic component.
   *
   * @extends Action
   * @category Action
   */
  class EditorUrlAction extends Action {
    /**
     * Creates a new instance of the EditorUrlAction class.
     *
     * @param {string} id - Unique identifier for the action.
     * @param {string} label - Label for the action.
     */
    constructor(id, label) {
      super(id, label);
      this.log = logger;
    }

    /**
     * Performs the EditorUrlAction by constructing the URL of the Visual Builder editor based on the provided
     * parameters.
     *
     * @param {Object} parameters - An object containing the necessary parameters to construct the editor URL.
     * @param {string} [parameters.editorUrl] - The base URL of the editor. If not provided, it defaults to the value
     * of `vbInitConfig.EDITOR_URL`.
     * @param {string} [parameters.componentId] - The ID of the component to edit. If provided, additional parameters
     * such as `componentId`, `ruleset`, and `dynamicLayout` are added to the URL.
     *
     * @returns {Promise<Outcome>} A promise resolving to the constructed editor URL.
     *
     * @throws {Error} If the `EDITOR_URL` property is missing from `vbInitConfig` or if the provided `editorUrl` is
     * invalid.
     */
    perform(parameters) {
      const initConfig = globalThis.vbInitConfig || {};
      const faConfig = globalThis.faConfig || {};
      const { application } = this.scopes;

      let url;

      // For DT testing, precedence is given to the editor URL stored in sessionStorage
      try {
        const { sessionStorage } = globalThis;
        url = new URL(sessionStorage && sessionStorage.getItem(Constants.SessionStorage.EDITOR_URL));
        // eslint-disable-next-line no-unused-vars
      } catch (err) {}

      if (!url) {
        let { editorUrl } = parameters;
        if (!editorUrl) {
          editorUrl = initConfig.EDITOR_URL;
          if (!editorUrl) {
            throw new Error('The property EDITOR_URL is missing from vbInitConfig.');
          }
        }

        try {
          // Construct the URL starting with the value from vbInitConfig
          url = new URL(editorUrl);
          // eslint-disable-next-line no-unused-vars
        } catch (err) {
          throw new Error(`Invalid Editor URL: ${editorUrl}`);
        }
      }

      // Strip existing request parameters
      url.search = '';

      // Make sure the path ends with '/'
      const path = Utils.addTrailingSlash(url.pathname);
      url.pathname = path;

      const { searchParams } = url;

      // Search always starts with ?_h=saas
      searchParams.set('_h', SAAS);

      // Set the "app" parameter
      let appId = initConfig.APP_ID;
      if (!appId) {
        appId = application.definition.id;
      }
      searchParams.set(PARAMS.APP_NAME, appId);

      // Set the "domain" parameter
      searchParams.set(PARAMS.DOMAIN, globalThis.location.host);

      // Set the "base" parameter
      const base = initConfig.BASE_URL || requirejs.toUrl('');
      searchParams.set(PARAMS.BASE, base);
      let appUrl = base;

      // Set the "appUrl" parameter
      if (Configuration.applicationUrl !== base) {
        appUrl = Configuration.applicationUrl;
        searchParams.set(PARAMS.APP_URL, appUrl);
      }

      // Set the "pageUrl" parameter
      let pageUrl = globalThis.location.href;
      // Shorten the page URL when it starts with the appUrl
      if (pageUrl.startsWith(appUrl)) {
        pageUrl = pageUrl.substring(appUrl.length);
      }
      searchParams.set(PARAMS.PAGE_URL, pageUrl);

      // Set the "sandbox" parameter
      const sandbox = Configuration.getSandboxId();
      if (sandbox) {
        searchParams.set(PARAMS.SANDBOX, sandbox);
      }

      // set the "stripe" parameter
      const stripe = faConfig.APP_STRIPE;
      if (stripe) {
        searchParams.set(PARAMS.STRIPE, stripe);
      }

      // Set the extension manager version when the app has an extension registry.
      if (application.extensionRegistry.hasRegistry) {
        // Version is always v2 (v1 support is gone)
        searchParams.set(PARAMS.EXT_MNGR, 'v2');
      }

      // set the "podName" parameter
      const podNameProperty = application.extensionRegistry.hasRegistry
        ? 'services.global.oraPodName'
        : 'services.catalog.common.faPodName';

      const podName = application.initParams[podNameProperty];
      if (podName) {
        searchParams.set(PARAMS.PODNAME, podName);
      }

      // Calls getCurrentPage with "true" to get leaf page. This guarantee to return the
      // correct leaf page even when inside a switcher.
      const leafPage = Router.getCurrentPage(true);
      if (leafPage) {
        // const leafPage = currentPage.getLeafContainer();
        // Use getNavPath() instead of fullPath for the value of the container path because
        // getNavPath() knows how to skip the hidden page of the fndShell.
        const leafPageNavPath = leafPage.getNavPath();
        // Set the "pageContainer" parameter
        searchParams.set(PARAMS.PAGE_CONT, leafPageNavPath);

        // Set the "pagePath" parameter
        searchParams.set(PARAMS.PAGE_PATH, `${leafPage.path}${leafPage.id}`);

        if (leafPage.package) {
          // Set the extension id to the id of extension defining the App UI page
          searchParams.set(PARAMS.EXT_ID, leafPage.extensionId);
        }
      }

      // Set the componentId the ruleset parameters
      const { componentId } = parameters;
      if (componentId) {
        // TODO: The search for the component should only be done in the view for
        // this page, not the entire document.
        const element = document.getElementById(componentId);
        if (element) {
          searchParams.set(PARAMS.COMPONENT_ID, componentId);
          searchParams.set(PARAMS.RULESET, element.getAttribute('layout'));
          const binding = element.getAttribute('metadata');
          if (binding) {
            const dynamicLayout = this.getDynamicLayout(binding);
            if (dynamicLayout) {
              searchParams.set(PARAMS.DYNAMIC_LAYOUT, dynamicLayout);
            } else {
              this.log.warn('Component', componentId,
                'is missing its DynamicLayoutMetadataProviderDescriptor definition');
            }
          }
        } else {
          this.log.warn('Component', componentId, 'is not found in the document.');
        }
      }

      this.log.info('Using VB editor URL:', url.href);

      // The action result is the URL to open the VB editor
      return Action.createSuccessOutcome(url.href);
    }

    /**
     * Retrieves the dynamic layout associated with a given component binding.
     *
     * @param {string} binding - The binding string of a component.
     * @returns {(string|null)} The dynamic layout endpoint, or null if not found.
     */
    getDynamicLayout(binding) {
      let ret;

      const mdpd = EditorUrlAction.getMetadataProviderFromBinding(binding);
      if (mdpd) {
        const { definition } = this.scopes.container;
        const mdpdDef = definition.metadata[mdpd];
        if (mdpdDef) {
          ret = mdpdDef.defaultValue && mdpdDef.defaultValue.endpoint;
        }
      }

      return ret;
    }

    /**
     * Returns the DynamicLayoutMetadataProviderDescriptor ID from a given component binding string.
     *
     * @param {string} binding - The binding string of a component, expected to be in the format
     * '[[$page.metadata.<provider>]]'.
     * @returns {string|null} The extracted DynamicLayoutMetadataProviderDescriptor ID, or null if not found.
     */
    static getMetadataProviderFromBinding(binding) {
      // TODO: Find a cleaner way to retrieve the metadata object
      const marker = '.metadata.';
      const mtIndex = binding.indexOf(marker);
      let ret;

      if (mtIndex) {
        const start = mtIndex + marker.length;
        ret = binding.substring(start, binding.indexOf('.', start));
      }

      return ret;
    }

    /**
     * Sets the context for the EditorUrlAction instance.
     *
     * @param {object} scopes - The scopes object containing relevant data for the action.
     */
    setContext(scopes) {
      this.scopes = scopes;
    }
  }

  return EditorUrlAction;
});

